import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import OtpInput from 'react-otp-input';
import 'react-phone-input-2/lib/style.css';
import { Phone, ArrowLeft, RefreshCw } from 'lucide-react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { isNative } from '../utils/platform';

const PhoneLogin = ({ onBack }) => {
  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [resendTimer, setResendTimer] = useState(0);
  const navigate = useNavigate();

  // Setup reCAPTCHA verifier (web only)
  useEffect(() => {
    if (isNative) return undefined;

    const setupRecaptcha = async () => {
      const { RecaptchaVerifier } = await import('firebase/auth');
      const { auth } = await import('../config/firebase');

      if (!window.recaptchaVerifier) {
        window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
          size: 'invisible',
          callback: () => {},
          'expired-callback': () => {
            setError('reCAPTCHA expired. Please try again.');
          },
        });
      }
    };

    setupRecaptcha();

    return () => {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
    };
  }, []);

  // Resend timer countdown
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const sendOTP = async () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formattedPhone = '+' + phoneNumber;

      if (isNative) {
        const { sendNativePhoneOtp } = await import('../services/nativeAuthService');
        const result = await sendNativePhoneOtp(formattedPhone);
        if (!result.success) {
          setError(result.error || 'Failed to send OTP. Please try again.');
          return;
        }
        setConfirmationResult({ native: true });
        setStep('otp');
        setResendTimer(60);
        return;
      }

      const { signInWithPhoneNumber } = await import('firebase/auth');
      const { auth } = await import('../config/firebase');
      const appVerifier = window.recaptchaVerifier;
      
      const confirmation = await signInWithPhoneNumber(auth, formattedPhone, appVerifier);
      setConfirmationResult(confirmation);
      setStep('otp');
      setResendTimer(60);
    } catch (err) {
      console.error('Error sending OTP:', err);
      if (err.code === 'auth/invalid-phone-number') {
        setError('Invalid phone number format');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many requests. Please try again later.');
      } else {
        setError(err.message || 'Failed to send OTP. Please try again.');
      }
      
      // Reset reCAPTCHA on error
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (isNative && confirmationResult?.native) {
        const formattedPhone = '+' + phoneNumber;
        const { confirmNativePhoneOtp } = await import('../services/nativeAuthService');
        const result = await confirmNativePhoneOtp(otp, formattedPhone);
        if (!result.success) {
          setError(result.error || 'Failed to verify OTP.');
          return;
        }

        const userDataWithRole = result.user || { role: 'owner' };
        if (userDataWithRole.role === 'caregiver') {
          navigate('/caregiver-dashboard', { replace: true });
        } else if (userDataWithRole.role === 'admin') {
          navigate('/admin-dashboard', { replace: true });
        } else {
          navigate('/owner-dashboard', { replace: true });
        }
        return;
      }

      // Verify OTP and sign in
      const result = await confirmationResult.confirm(otp);
      const user = result.user;

      // Get or create user document
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        // Create new user document
        await setDoc(userRef, {
          uid: user.uid,
          phoneNumber: user.phoneNumber,
          displayName: user.phoneNumber,
          role: 'owner', // Default role
          createdAt: serverTimestamp(),
          authMethod: 'phone',
          status: 'active'
        }, { merge: true });
      }

      // Get user data with role
      const userData = await getDoc(userRef);
      const userDataWithRole = userData.exists() 
        ? { ...userData.data(), uid: user.uid }
        : { role: 'owner', uid: user.uid };

      // Navigate based on role
      if (userDataWithRole.role === 'caregiver') {
        navigate('/caregiver-dashboard', { replace: true });
      } else if (userDataWithRole.role === 'admin') {
        navigate('/admin-dashboard', { replace: true });
      } else {
        navigate('/owner-dashboard', { replace: true });
      }
    } catch (err) {
      console.error('Error verifying OTP:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);
      
      if (err.code === 'auth/invalid-verification-code') {
        setError('Invalid OTP. Please check and try again.');
      } else if (err.code === 'auth/code-expired') {
        setError('OTP expired. Please request a new one.');
      } else if (err.code === 'permission-denied') {
        setError('Permission denied. Please check Firestore security rules.');
      } else if (err.message && (err.message.includes('offline') || err.message.includes('client is offline'))) {
        setError('Firestore permission issue. This is likely a security rules problem, not your internet. Check console for details.');
      } else if (err.message && err.message.includes('Missing or insufficient permissions')) {
        setError('Firestore permissions not configured correctly. Please update security rules.');
      } else {
        setError(`Error: ${err.message || 'Failed to verify OTP'}. Check console for details.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    
    setOtp('');
    setError('');

    if (isNative) {
      const formattedPhone = '+' + phoneNumber;
      const { resendNativePhoneOtp } = await import('../services/nativeAuthService');
      const result = await resendNativePhoneOtp(formattedPhone);
      if (!result.success) {
        setError(result.error || 'Failed to resend OTP.');
        return;
      }
      setResendTimer(60);
      return;
    }
    
    // Reset to phone step and resend
    setStep('phone');
    await sendOTP();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (step === 'otp') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-[var(--pf-accent-soft)] rounded-full flex items-center justify-center mx-auto mb-4">
            <Phone className="w-8 h-8 text-[var(--pf-accent)]" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Enter OTP</h3>
          <p className="text-gray-600">
            We've sent a 6-digit code to
          </p>
          <p className="text-gray-900 font-semibold mt-1">
            +{phoneNumber}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="flex justify-center">
          <OtpInput
            value={otp}
            onChange={setOtp}
            numInputs={6}
            renderSeparator={<span className="mx-2"></span>}
            renderInput={(props) => (
              <input
                {...props}
                className="!w-12 h-14 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:border-[var(--pf-accent)] focus:outline-none"
              />
            )}
            shouldAutoFocus
          />
        </div>

        <button
          onClick={verifyOTP}
          disabled={loading || otp.length !== 6}
          className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Verifying...' : 'Verify & Sign In'}
        </button>

        <div className="text-center space-y-3">
          {resendTimer > 0 ? (
            <p className="text-sm text-gray-600">
              Resend OTP in {formatTime(resendTimer)}
            </p>
          ) : (
            <button
              onClick={handleResendOTP}
              className="text-sm text-[var(--pf-accent)] hover:opacity-80 font-medium flex items-center justify-center gap-2 mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              Resend OTP
            </button>
          )}

          <button
            onClick={() => {
              setStep('phone');
              setOtp('');
              setError('');
            }}
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center justify-center gap-2 mx-auto"
          >
            <ArrowLeft className="w-4 h-4" />
            Change phone number
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-[var(--pf-accent-soft)] rounded-full flex items-center justify-center mx-auto mb-4">
          <Phone className="w-8 h-8 text-[var(--pf-accent)]" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Sign in with Phone</h3>
        <p className="text-gray-600">
          Enter your phone number to receive an OTP
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Phone Number
        </label>
        <PhoneInput
          country={'in'}
          value={phoneNumber}
          onChange={setPhoneNumber}
          inputClass="!w-full !h-12 !text-base"
          containerClass="!w-full"
          buttonClass="!h-12"
          enableSearch
          searchPlaceholder="Search country"
          preferredCountries={['in', 'us', 'gb']}
        />
      </div>

      <button
        onClick={sendOTP}
        disabled={loading || !phoneNumber}
        className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Sending OTP...' : 'Send OTP'}
      </button>

      {onBack && (
        <button
          onClick={onBack}
          className="w-full text-sm text-gray-600 hover:text-gray-900 flex items-center justify-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login options
        </button>
      )}

      {/* Invisible reCAPTCHA container (web only) */}
      {!isNative && <div id="recaptcha-container"></div>}
    </div>
  );
};

export default PhoneLogin;

